// Компонент для input или textarea с анимированным плейсхолдером
// и опциональным выводом ошибки/вспомогательного текста
// Может быть в базовом варианте (для демо-форм) и в small варианте (во всех остальных местах)
// Скрывает ошибку под полем при blur
// Работает с подсказками
// Может отправлять событие notBounce в метрику при prop notBounce = true

import { nextTick } from "vue";
import sugs from "./suggestions";
import Analytics from "../marketing/analytics";

const template = /* html */`
    <div class="flex-column gap-0_25">
        <label
            :class="[
                'labeled-input-wrap',
                'flex-auto',
                disabled ? 'labeled-input-disabled' : '',
                small ? 'labeled-input-small' : '',
                readonly ? 'labeled-input-readonly' : '',
                alwaysMobile ? 'labeled-input-mobile' : '',
                visibleError ? 'labeled-input--with-error' : '',
            ]"
            tabindex="1"
        >
            <div class="labeled-input-group">
                <span
                    class="invisible labeled-input-fake-label"
                    aria-hidden="true"
                >[[ label ]]</span>
                <component
                    :is="textarea ? 'textarea' : 'input'"
                    :name="inputName"
                    :type="inputType"
                    :class="['labeled-input mt-0_25', small ? '' : 'mt-sm-0_5']"
                    ref="inputRef"
                    :disabled="disabled"
                    :readonly="readonly"
                    :required="required"
                    @input="handleInput"
                    :id="inputId || null"
                    placeholder=" "
                    :data-id="inputSelector"
                    v-bind="{
                        ...(value !== null ? {value: value} : {})
                    }"
                    :autocomplete="autocomplete || null"
                />
                <span
                    :class="[
                        'labeled-input-label',
                        'color-gray',
                        textarea ? 'labeled-input-label--textArea' : ''
                    ]"
                    data-test="label"
                >[[ label ]]</span>
            </div>
        </label>
        <transition name="slide-in">
            <div
                v-if="visibleError || helptext"
                class="labeled-input-helptext__container"
            >
                <transition name="slide-in">
                    <span
                        class="labeled-input-helptext"
                        v-if="!visibleError && helptext"
                    >
                        [[ helptext ]]
                    </span>
                    <span
                        class="labeled-input-helptext labeled-input-error"
                        v-else-if="visibleError"
                    >
                        [[ visibleError ]]
                    </span>
                </transition>
            </div>
        </transition>
    </div>
`;

export default {
    template,
    props: {
        label: {
            type: String,
            required: true,
        },
        textarea: {
            type: Boolean,
            default: false,
        },
        required: {
            type: Boolean,
            default: false,
        },
        isFocused: {
            type: Boolean,
            default: false,
        },
        value: {
            type: [String, Number, null],
            default: null,
        },
        inputName: {
            type: String,
            default: "text",
        },
        inputType: {
            type: String,
            default: "text",
        },
        inputId: {
            type: String,
            default: "",
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        suggest: {
            type: String,
            default: "",
        },
        suggestCallbacks: {
            type: Object,
            default: () => {},
        },
        suggestOptions: {
            type: Object,
            default: () => {},
        },
        clearSuggestions: {
            type: Boolean,
            default: false,
        },
        helptext: {
            type: String,
            default: "",
        },
        error: {
            type: String,
            default: "",
        },
        small: {
            type: Boolean,
            default: false,
        },
        alwaysMobile: {
            type: Boolean,
            default: false,
        },
        notBounce: {
            type: Boolean,
            default: false,
        },
        autocomplete: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            showError: true,
            inputSelector: `${Math.random()}`,
        };
    },
    computed: {
        visibleError() {
            return this.showError ? this.error : null;
        },
        suggestionsData() {
            return {
                type: this.suggest,
                callbacks: this.suggestCallbacks,
                options: this.suggestOptions,
            };
        },
        callbacks() {
            return {
                ...this.suggestCallbacks,
                onSelect: this.customSelect,
            };
        },
        input() {
            return this.$refs.inputRef;
        },
    },
    expose: ["input"],
    watch: {
        error() {
            this.showError = true;
        },
        isFocused() {
            nextTick(() => {
                this.focus();
            });
        },
        suggestionsData: {
            handler(newVal) {
                const suggestions = $(this.$refs.inputRef)
                    .suggestions();

                if (suggestions) {
                    const boundedCallbacks = Object.keys(this.callbacks)
                        .reduce((acc, cur) => {
                            acc[cur] = (...args) => this.callbacks[cur](newVal.type, ...args);
                            return acc;
                        }, {});
                    suggestions.setOptions({
                        type: newVal.type.toUpperCase(),
                        ...boundedCallbacks,
                        ...newVal.options,
                    });
                } else {
                    sugs.initForElement(
                        this.$refs.inputRef,
                        newVal.type,
                        this.callbacks,
                        newVal.options,
                    );
                }
            },
            deep: true,
        },
        clearSuggestions(newVal) {
            if (!newVal) return;
            const suggestions = $(this.input)
                .suggestions();
            if (suggestions) {
                suggestions.clear();
            }
        },
    },
    methods: {
        handleInput(e) {
            if (this.disabled || this.readonly) return;
            this.$emit("input", e);
            this.showError = false;
        },
        focus() {
            if (this.isFocused && this.$refs.inputRef) {
                this.$refs.inputRef.focus({ preventScroll: true });

                setTimeout(() => {
                    if (!this.isFocused || !this.$refs.inputRef) return;
                    const type = this.$refs.inputRef.getAttribute("type");
                    this.$refs.inputRef.setAttribute("type", "text");
                    this.$refs.inputRef.selectionStart = this.$refs.inputRef.value.length;
                    this.$refs.inputRef.setAttribute("type", type);
                }, 0);
            }
        },
        customSelect(...args) {
            this.$refs.inputRef.dispatchEvent(
                new InputEvent("input", { bubbles: true }),
            );
            if (this.suggestCallbacks?.onSelect) {
                this.suggestCallbacks.onSelect(...args);
            }
        },
    },
    emits: ["input"],
    mounted() {
        nextTick(() => {
            this.focus();
        });
        if (this.suggest) {
            sugs.initForElement(
                this.$refs.inputRef,
                this.suggest,
                this.callbacks,
                this.suggestOptions,
            );
            this.$refs.inputRef.setAttribute("autocomplete", this.autocomplete || "off");
        }
        if (this.notBounce) {
            Analytics.fireNotBounceOnEvent(`[data-id='${this.inputSelector}']`, "input");
        }
    },
};
